import { Injectable, OnDestroy } from "@angular/core";
import { HttpBackend, HttpClient } from "@angular/common/http";
import { BehaviorSubject, interval, Observable, Subscription } from "rxjs";
import { timeout } from "rxjs/operators";

import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class NetworkService implements OnDestroy {
  private readonly pollingInterval = 30000; // 30 seconds while confirmed offline
  // A real request either succeeds or it doesn't - "slow" is not "offline".
  // This is just long enough to not flag a busy-but-working connection,
  // short enough that a genuinely dead connection doesn't leave the UI
  // guessing for a long time.
  private readonly healthCheckTimeout = 8000; // ms

  private onlineStatus$ = new BehaviorSubject<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  private pollingSubscription: Subscription | null = null;
  private isCheckingNetwork = false; // Flag to prevent overlapping checks
  // A client built straight from HttpBackend, bypassing every
  // HttpInterceptor (AuthInterceptor in particular). A plain connectivity
  // ping needs no auth header, and going through AuthInterceptor lazily
  // injects AuthService -> ... -> CacheService -> NetworkService again -
  // if this constructor's own initial check is the very first thing to
  // touch that chain, Angular throws NG0200 (circular DI) and the health
  // check silently "fails" every time regardless of actual connectivity.
  // That's the real explanation behind "detection feels buggy": it wasn't
  // reading the network, it was failing on a DI cycle before the request
  // ever went out.
  private readonly directHttp: HttpClient;
  private readonly boundHandleBrowserOnline = () => this.handleBrowserOnline();
  private readonly boundHandleBrowserOffline = () =>
    this.handleBrowserOffline();

  constructor(private http: HttpClient, httpBackend: HttpBackend) {
    console.log("Network service initialized.");
    this.directHttp = new HttpClient(httpBackend);

    // The browser/WebView's own online/offline events are an instant,
    // free signal (no network round trip) - use them as the primary
    // trigger instead of only ever finding out via a 30s poll. They can
    // still be wrong in either direction (e.g. "online" on a captive
    // portal with no real internet), so a real request always backs them
    // up rather than being trusted blindly.
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.boundHandleBrowserOnline);
      window.addEventListener("offline", this.boundHandleBrowserOffline);
    }

    // Deferred a tick past construction - belt-and-suspenders alongside
    // directHttp above, so this never again races Angular's DI graph
    // while NetworkService itself is still being built.
    setTimeout(() => {
      this.checkInternetConnection().catch((error) => {
        console.error("Error during initial connection check:", error);
      });
    }, 0);
  }

  private handleBrowserOnline() {
    console.log("Browser reported online - verifying with a real request.");
    this.checkInternetConnection().catch((error) =>
      console.error("Error verifying connection after online event:", error)
    );
  }

  private handleBrowserOffline() {
    console.log("Browser reported offline.");
    this.markInternetDown();
  }

  /**
   * Mark the internet as down and start polling.
   */
  public markInternetDown() {
    if (this.onlineStatus$.value) {
      this.onlineStatus$.next(false);
    }

    if (!this.pollingSubscription) {
      this.startPolling();
    }
  }

  public getOnlineStatus(): Observable<boolean> {
    return this.onlineStatus$.asObservable();
  }

  /**
   * Check if the application is online.
   */
  public isOnline(): boolean {
    return this.onlineStatus$.value;
  }

  /**
   * Externally callable method to check internet connection. A single
   * lightweight health-check request, not a mandatory file download plus
   * bandwidth benchmark - the old version treated "slow" the same as
   * "offline" against fixed latency/bandwidth thresholds, which is why
   * this flagged the app offline (hiding features, showing the offline
   * banner) on connections that were merely slow but working fine.
   */
  public async checkInternetConnection(): Promise<boolean> {
    if (this.isCheckingNetwork) {
      console.log("Network check is already in progress. Ignoring.");
      return this.onlineStatus$.value; // Return the current status
    }

    this.isCheckingNetwork = true;

    try {
      const reachable = await this.pingHealthCheck();

      if (!reachable) {
        console.warn("Health check unreachable. Marking offline.");
        this.markInternetDown();
        return false;
      }

      if (!this.onlineStatus$.value) {
        this.onlineStatus$.next(true);
      }
      this.stopPolling();
      return true;
    } catch (error) {
      console.error("Error during connection check:", error);
      this.markInternetDown();
      return false;
    } finally {
      this.isCheckingNetwork = false;
    }
  }

  /**
   * A single fast request to the API's health check endpoint, bounded by
   * a timeout. Success (any non-error HTTP response) = online. A network
   * error or timeout = offline. That's the whole check - no latency
   * averaging, no file download, no throughput math. Uses directHttp
   * (see constructor) so this never depends on the auth/interceptor chain.
   *
   * Success is "the request resolved without throwing", not "the body was
   * non-null" - /api/healthcheck responds 204 with no body, which some
   * response parsing paths surface as a null value even on a genuine
   * success, so checking the body itself would misreport a working
   * connection as offline.
   */
  private async pingHealthCheck(): Promise<boolean> {
    const healthCheckUrl = `${environment.apiUrl}/api/healthcheck`;

    try {
      await this.directHttp
        .get(healthCheckUrl, { responseType: "text", observe: "response" })
        .pipe(timeout(this.healthCheckTimeout))
        .toPromise();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Poll while offline to notice recovery - stopped as soon as a check
   * succeeds (see checkInternetConnection), not left running forever.
   */
  private startPolling() {
    this.pollingSubscription = interval(this.pollingInterval).subscribe(() => {
      this.checkInternetConnection();
    });
  }

  private stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.boundHandleBrowserOnline);
      window.removeEventListener("offline", this.boundHandleBrowserOffline);
    }
  }

  isNetworkError(error: any): boolean {
    if (error.status === 0) {
      return true;
    }

    const errorMessage = error.message || error.toString();
    if (
      errorMessage.includes("ERR_INTERNET_DISCONNECTED") ||
      errorMessage.includes("NetworkError") ||
      errorMessage.includes("Failed to fetch")
    ) {
      return true;
    }

    return false;
  }
}
